import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectInlineEdit from "@components/objects/ObjectInlineEdit";
import { useCreatePublicImprovement, useDeletePublicImprovement, useGetAllPublicImprovements, useUpdatePublicImprovement } from "@api/publicImprovementService";
import { formatCurrency } from "@utils/numbers";
import { formatDate } from "@utils/dates";
import { useCreateMembeshipLevel, useDeleteMembeshipLevel, useGetAllMembeshipLevels, useUpdateMembeshipLevel } from "@api/membershipLevelService";
import { useCreateTraining, useDeleteTraining, useGetAllTrainings, useUpdateTraining } from "@api/trainingService";
import { useCreateAppeal, useDeleteAppeal, useGetAllAppeals, useUpdateAppeal } from "@api/appealService";
import { useCreateCampaign, useDeleteCampaign, useGetAllCampaigns, useUpdateCampaign } from "@api/campaignService";
import { useCreatePark, useDeletePark, useGetAllParks, useUpdatePark } from "@api/parksService";
import { MapModal } from "@components/MapModal";
import { useCreatePublicParking, useDeletePublicParking, useGetAllPublicParking, useUpdatePublicParking } from "@api/publicParkingService";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { yesNo } from "@utils/yesNo";

const columns = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: true,
        label: 'Name',
        type: 'text',
        placeholder: 'e.g. Washington Park',
        config: {
            required: true,
        },
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
            </>)
        }
      },
      {
        id: 'location',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Location',
        type: 'text',
        placeholder: 'e.g. 42nd & Maine Street',
        cell: (row) => row.location,
      },
      {
        id: 'latitude',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Coordinates',
        type: 'text',
        placeholder: 'Latitude',
        tooltip: 'Latitude',
        cell: (row) => {
            if(!row.latitude || !row.longitude) {
                return null;
            }

            return (<>
                {row.latitude}, {row.longitude}
                <br/>
                <MapModal position={{lat: row.latitude, lng: row.longitude}}/>
            </>);
        },
        additionalEditFields: [
            {
                id: 'longitude',
                placeholder: 'Longitude',
                type: 'text',
                tooltip: 'Longitude',
                // tooltip: 'If private and public dollars were used, use two entries',
            }
        ],
      },
      {
        id: 'public_spots',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Parking Spots',
        type: 'text',
        placeholder: 'Public Spots',
        // endDecorator: 'Public',
        tooltip: 'Public',
        cell: (row) => {
            return (<>
            <FauxTable>
                <FauxTableRow title={'Public'}>{row.public_spots}</FauxTableRow>
                <FauxTableRow title={'Hourly'}>{row.hourly_spots}</FauxTableRow>
                <FauxTableRow title={'Accessible'}>{row.handicapped_spots}</FauxTableRow>
                <FauxTableRow title={'Leased'}>{row.leased_spots}</FauxTableRow>
            </FauxTable>
            </>);
        },
        additionalEditFields: [
            {
                id: 'hourly_spots',
                placeholder: 'Hourly Spots',
                tooltip: 'Hourly',
                // endDecorator: 'Hourly',
                type: 'text',
            },
            {
                id: 'handicapped_spots',
                placeholder: 'Accessible Spots',
                tooltip: 'Accessible',
                // endDecorator: 'Accessible',
                type: 'text',
            },
            {
                id: 'leased_spots',
                placeholder: 'Leased Spots',
                tooltip: 'Leased',
                // endDecorator: 'Leased',
                type: 'text',
            },
        ],
      },
      {
        id: 'on_street',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'On Street Parking',
        type: 'boolean',
        cell: (row) => yesNo(row?.on_street),
      },
];

const listCell = (row, {LinkComponent}) => {
    return (<>
    {row.name !== row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {row.company_name && (<Typography fontSize="sm">{row.company_name}</Typography>)}
        </Box>
    )}
    {row.name === row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.company_name}
                </LinkComponent>
            </Typography>
        </Box>
        )}
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <Tooltip title="Email">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!row.email}
                component={'a'}
                href={`mailto:${row.email}`}
                >
                <EmailRoundedIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Call">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!getPreferredPhone(row)}
                component={'a'}
                href={`tel:${getPreferredPhone(row)}`}
                >
                <LocalPhoneRoundedIcon />
            </IconButton>
        </Tooltip>
    </>);
};

export default function PublicParking() {
    return (
        <ObjectInlineEdit
            query={useGetAllPublicParking}
            createMutation={useCreatePublicParking}
            updateMutation={useUpdatePublicParking}
            deleteMutation={useDeletePublicParking}

            type={'public_parking'}
            endpoint={'public-parking'}
            permissionType={'settings'}
            title={'Public Parking'}
            color={'default'}
            columns={columns}
            listCell={listCell}
            listActions={listActions}
        />
    )
}
